/**
 * Pure land-generation functions.
 * No side effects, no store imports, no hooks.
 * All functions are deterministic given the same inputs.
 */
import { isLand } from '../utils/card-helpers.js';
import { LAND_CATALOG, BASIC_LAND_NAMES, BASIC_TYPE_TO_COLOR } from './land-catalog.js';
// ─── Constants ────────────────────────────────────────────────────────────────
const ALL_COLORS = ['W', 'U', 'B', 'R', 'G'];
// Heuristic hint: suggested land count lookup table
// Rows: avg CMC buckets; Columns: cheap draw count buckets
// [cheapDraw >= 6, cheapDraw 3-5, cheapDraw 0-2]
const HINT_TABLE = [
    { maxCmc: 2.5, counts: [33, 34, 35] },
    { maxCmc: 3.5, counts: [34, 35, 36] },
    { maxCmc: 4.0, counts: [35, 36, 37] },
    { maxCmc: Infinity, counts: [36, 37, 38] },
];
// ─── Eligible Cards ───────────────────────────────────────────────────────────
/**
 * Get the cards from a cycle that are eligible for the given color identity.
 *
 * Eligibility rules:
 * - Non-fetch lands: at least one produced color must be in the color identity.
 * - Fetch lands: at least one fetchType must match a basic type in the identity.
 * - Triomes: requires at least 2 of the triome's 3 colors to be in the identity,
 *   AND requires the identity to have 3+ colors (enforced via requiresThreeColors).
 * - Path of Ancestry: requires hasTribalSupport === true.
 *
 * @param cycle - The land cycle to filter.
 * @param colorIdentity - The deck's color identity.
 * @param hasTribalSupport - Whether tribal detection passed.
 * @returns Eligible entries from the cycle.
 */
export function getEligibleCards(cycle, colorIdentity, hasTribalSupport) {
    // Guard: triomes require 3+ color identity
    if (cycle.requiresThreeColors && colorIdentity.length < 3) {
        return [];
    }
    // Guard: path-of-ancestry requires tribal support
    if (cycle.requiresTribal && !hasTribalSupport) {
        return [];
    }
    const identitySet = new Set(colorIdentity);
    return cycle.entries.filter((entry) => {
        // Fetch lands: classify based on how many fetchTypes match the identity
        if (entry.fetchTypes && entry.fetchTypes.length > 0) {
            const matchCount = entry.fetchTypes.filter((t) => {
                const color = BASIC_TYPE_TO_COLOR[t];
                return color !== undefined && identitySet.has(color);
            }).length;
            if (cycle.category === 'fetch-on') {
                // On-color: both fetchable types must match the identity
                return matchCount === entry.fetchTypes.length;
            }
            if (cycle.category === 'fetch-off') {
                // Off-color: exactly one fetchable type matches (the other doesn't)
                return matchCount >= 1 && matchCount < entry.fetchTypes.length;
            }
            // Fallback for any other category with fetchTypes
            return matchCount >= 1;
        }
        // Triomes: ALL triome colors must be within the color identity.
        // Commander rules: you cannot run a card with colors outside your identity.
        if (entry.triomeColors && entry.triomeColors.length > 0) {
            return entry.triomeColors.every((c) => identitySet.has(c));
        }
        // Path of Ancestry: produces nothing specific — always eligible once tribal passes
        if (entry.produces.length === 0) {
            return true;
        }
        // Regular lands: ALL produced colors must be within the color identity.
        // A UG deck can't run a WB shock land — that's outside its color identity.
        return entry.produces.every((c) => identitySet.has(c));
    });
}
// ─── On/Off Color Fetch Classification ───────────────────────────────────────
/**
 * Classify fetch land entries as on-color vs off-color for the given identity.
 *
 * On-color: both fetchTypes match colors in the identity.
 * Off-color: exactly one fetchType matches a color in the identity.
 *
 * @param entries - The fetch land entries to classify.
 * @param colorIdentity - The deck's color identity.
 * @returns An object with onColor and offColor arrays.
 */
export function computeOnOffColorFetch(entries, colorIdentity) {
    const identitySet = new Set(colorIdentity);
    const onColor = [];
    const offColor = [];
    for (const entry of entries) {
        if (!entry.fetchTypes || entry.fetchTypes.length === 0)
            continue;
        const matchingTypes = entry.fetchTypes.filter((t) => {
            const color = BASIC_TYPE_TO_COLOR[t];
            return color !== undefined && identitySet.has(color);
        });
        if (matchingTypes.length >= 2) {
            onColor.push(entry);
        }
        else if (matchingTypes.length === 1) {
            offColor.push(entry);
        }
        // matchingTypes.length === 0: not eligible at all — skip
    }
    return { onColor, offColor };
}
// ─── Heuristic Hint ───────────────────────────────────────────────────────────
/**
 * Compute a heuristic land count hint based on deck characteristics.
 *
 * Cheap draw detection: cards with CMC <= 2 that have "draw" keyword,
 * or whose oracle text contains "draw a card" or "draw two cards".
 *
 * @param entries - The full deck entries.
 * @returns A HeuristicHint with suggested land count and explanation data.
 */
export function computeHeuristicHint(entries) {
    const nonLandEntries = entries.filter((e) => !e.isCommander && !isLand(e.card));
    // Average CMC across all non-land, non-commander cards
    const totalCards = nonLandEntries.reduce((s, e) => s + e.quantity, 0);
    const totalCmc = nonLandEntries.reduce((s, e) => s + e.card.cmc * e.quantity, 0);
    const avgCmc = totalCards > 0 ? totalCmc / totalCards : 0;
    // Cheap draw count: non-land cards with CMC <= 2 that draw cards
    const cheapDrawCount = nonLandEntries
        .filter((e) => {
        if (e.card.cmc > 2)
            return false;
        const hasDraw = e.card.keywords.some((k) => k.toLowerCase() === 'draw');
        const oracleText = (e.card.oracleText ?? '').toLowerCase();
        const hasDrawText = oracleText.includes('draw a card') ||
            oracleText.includes('draw two cards') ||
            oracleText.includes('draw cards');
        return hasDraw || hasDrawText;
    })
        .reduce((s, e) => s + e.quantity, 0);
    // Look up the suggested count from the table
    const row = HINT_TABLE.find((r) => avgCmc <= r.maxCmc) ?? HINT_TABLE[HINT_TABLE.length - 1];
    const colIndex = cheapDrawCount >= 6 ? 0 : cheapDrawCount >= 3 ? 1 : 2;
    const suggestedCount = row.counts[colIndex];
    return { avgCmc, cheapDrawCount, suggestedCount };
}
// ─── Tribal Support Detection ─────────────────────────────────────────────────
/**
 * Detect whether the deck has tribal support for Path of Ancestry.
 *
 * Returns true if:
 * 1. At least one commander has creature subtypes (words after "—" in type line).
 * 2. At least one non-commander card shares one of those subtypes.
 *
 * @param entries - The full deck entries.
 * @returns True if tribal support detected.
 */
export function detectTribalSupport(entries) {
    const commanders = entries.filter((e) => e.isCommander);
    const nonCommanders = entries.filter((e) => !e.isCommander);
    // Extract creature subtypes from commanders
    const commanderSubtypes = new Set();
    for (const { card } of commanders) {
        const typeLine = card.typeLine;
        const dashIndex = typeLine.indexOf('—');
        if (dashIndex === -1)
            continue;
        const subtypePart = typeLine.slice(dashIndex + 1).trim();
        const subtypes = subtypePart.split(/\s+/).filter((s) => s.length > 0);
        for (const s of subtypes) {
            commanderSubtypes.add(s.toLowerCase());
        }
    }
    if (commanderSubtypes.size === 0)
        return false;
    // Check if any non-commander shares a subtype
    for (const { card } of nonCommanders) {
        const typeLine = card.typeLine;
        const dashIndex = typeLine.indexOf('—');
        if (dashIndex === -1)
            continue;
        const subtypePart = typeLine.slice(dashIndex + 1).trim();
        const subtypes = subtypePart.split(/\s+/).filter((s) => s.length > 0);
        for (const s of subtypes) {
            if (commanderSubtypes.has(s.toLowerCase()))
                return true;
        }
    }
    return false;
}
// ─── Simple category allocation builder (no sim, for Stage 1 display only) ───
/**
 * Build a simple list of category allocations for Stage 1 display.
 * Does NOT compute actual allocation counts — just returns maxAvailable per enabled category.
 * The simulation determines actual counts; this is only for the checkbox display.
 *
 * @param enabledCategories - Currently enabled land categories.
 * @param colorIdentity - The deck's color identity.
 * @param hasTribalSupport - Whether tribal detection passed.
 * @returns Allocations with proposedCount=0 (actual allocation is done by sim).
 */
export function buildCategoryAllocations(enabledCategories, colorIdentity, hasTribalSupport) {
    const allocations = [];
    for (const cycle of LAND_CATALOG) {
        if (cycle.category === 'basic')
            continue;
        if (cycle.category === 'rainbow')
            continue;
        const eligible = getEligibleCards(cycle, colorIdentity, hasTribalSupport);
        if (eligible.length === 0)
            continue;
        allocations.push({
            category: cycle.category,
            label: cycle.label,
            proposedCount: 0,
            maxAvailable: eligible.length,
        });
    }
    // Sort: enabled categories first, then alphabetically
    allocations.sort((a, b) => {
        const aEnabled = enabledCategories.includes(a.category);
        const bEnabled = enabledCategories.includes(b.category);
        if (aEnabled !== bEnabled)
            return aEnabled ? -1 : 1;
        return a.label.localeCompare(b.label);
    });
    return allocations;
}
// ─── Re-exports for convenience ───────────────────────────────────────────────
export { LAND_CATALOG, BASIC_LAND_NAMES, ALL_COLORS };
//# sourceMappingURL=land-generator.js.map